/**
 * HomePage Component
 * 
 * Main landing page for the Chronos Watch Shop featuring:
 * - Hero section with featured products
 * - Product carousel showcasing the collection
 * - Customer testimonials
 * - Newsletter subscription
 * - Live chat support widget
 * 
 * Data is fetched from the backend API if available,
 * otherwise falls back to mock data for static deployments.
 */

import { useState, useEffect } from 'react';
import axios from 'axios';

// Layout Components
import Header from '@/components/Header';
import Hero from '@/components/Hero';
import Footer from '@/components/Footer';

// Feature Components
import ProductCarousel from '@/components/ProductCarousel';
import Testimonials from '@/components/Testimonials';
import Newsletter from '@/components/Newsletter';
import CartDrawer from '@/components/CartDrawer';
import LiveChat from '@/components/LiveChat';
import SizeGuide from '@/components/SizeGuide';

// Mock Data (for static deployment without backend)
import { MOCK_PRODUCTS, MOCK_TESTIMONIALS } from '@/data/mockData';

// API Configuration
const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : null;

const HomePage = () => {
  // State
  const [products, setProducts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [isSizeGuideOpen, setIsSizeGuideOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch data on mount
  useEffect(() => {
    const fetchData = async () => {
      // Use mock data for static deployment (no backend)
      if (!API) {
        setProducts(MOCK_PRODUCTS);
        setTestimonials(MOCK_TESTIMONIALS);
        setLoading(false);
        return;
      }

      // Fetch from API
      try {
        const [productsRes, testimonialsRes] = await Promise.all([
          axios.get(`${API}/products`),
          axios.get(`${API}/testimonials`)
        ]);
        setProducts(productsRes.data);
        setTestimonials(testimonialsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        // Fallback to mock data on error
        setProducts(MOCK_PRODUCTS);
        setTestimonials(MOCK_TESTIMONIALS);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="text-gold-500 font-serif text-2xl animate-pulse">
          CHRONOS
        </div>
      </div>
    );
  }

  // Main Render
  return (
    <div className="min-h-screen bg-obsidian" data-testid="homepage">
      {/* Navigation */}
      <Header onSizeGuideClick={() => setIsSizeGuideOpen(true)} />

      {/* Main Content */}
      <Hero products={products} />
      <ProductCarousel
        products={products}
        onSizeGuideClick={() => setIsSizeGuideOpen(true)}
      />
      <Testimonials testimonials={testimonials} />
      <Newsletter />
      <Footer />

      {/* Overlays & Widgets */}
      <CartDrawer />
      <LiveChat />
      <SizeGuide
        isOpen={isSizeGuideOpen}
        onClose={() => setIsSizeGuideOpen(false)}
      />
    </div>
  );
};

export default HomePage;
